package com.patronat.top.dto

import com.patronat.top.dto.venues.VenueCategory
import com.patronat.top.dto.venues.VenueGroup
import com.patronat.top.dto.venues.VenueLocation
import com.patronat.top.dto.venues.VenueMediaRef
import com.patronat.top.dto.venues.VenueSchedule
import com.patronat.top.dto.venues.VenueServices
import com.patronat.top.dto.venues.VenueStatus
import kotlinx.datetime.Instant
import kotlinx.serialization.Serializable

const val UNASSIGNED_VENUE_OWNER_ID = "00000000-0000-0000-0000-000000000000"

@Serializable
data class CreateVenueRequest(
    val name: String,
    val description: String? = null,
    val category: VenueCategory,
    val phone: String,
    val website: String? = null,
    val email: String? = null,
    val location: VenueLocation,
    val schedule: VenueSchedule,
    val services: VenueServices,
    val gallery: List<VenueMediaRef> = emptyList(),
    val galleryMediaIds: List<String> = emptyList(),
    val submitForModeration: Boolean = true
)

@Serializable
data class UpdateVenueRequest(
    val name: String,
    val description: String? = null,
    val category: VenueCategory,
    val phone: String,
    val website: String? = null,
    val email: String? = null,
    val location: VenueLocation,
    val schedule: VenueSchedule,
    val services: VenueServices,
    val gallery: List<VenueMediaRef> = emptyList(),
    val galleryMediaIds: List<String> = emptyList(),
    val status: VenueStatus? = null
)

@Serializable
data class PatchVenueRequest(
    val name: String? = null,
    val description: String? = null,
    val category: VenueCategory? = null,
    val phone: String? = null,
    val website: String? = null,
    val email: String? = null,
    val location: VenueLocation? = null,
    val schedule: VenueSchedule? = null,
    val services: VenueServices? = null,
    val gallery: List<VenueMediaRef>? = null,
    val galleryMediaIds: List<String>? = null,
    val submitForModeration: Boolean = false
)

@Serializable
enum class VenueDraftState {
    DRAFT,
    APPLICATION,
    APPROVED,
    REJECTED
}

@Serializable
enum class VenueDraftAiStatus {
    PENDING,
    PROCESSING,
    DONE,
    ERROR
}

@Serializable
enum class VenueDraftAiDecision {
    PASS,
    WARN,
    FAIL
}

@Serializable
enum class VenueDraftAiSeverity {
    WARNING,
    CRITICAL
}

@Serializable
enum class VenueDraftAiSource {
    REKOGNITION,
    BEDROCK,
    SYSTEM
}

@Serializable
data class VenueAiFinding(
    val fieldPath: String,
    val severity: VenueDraftAiSeverity,
    val reason: String,
    val confidence: Double? = null,
    val source: VenueDraftAiSource
)

@Serializable
data class VenueDraftResponse(
    val id: String,
    val venueId: String,
    val requesterOwnerId: String,
    val requestedOwnerId: String? = null,
    val payload: UpdateVenueRequest,
    val state: VenueDraftState,
    val aiStatus: VenueDraftAiStatus? = null,
    val aiDecision: VenueDraftAiDecision? = null,
    val aiFindings: List<VenueAiFinding> = emptyList(),
    val aiCompletedAt: Instant? = null,
    val aiError: String? = null,
    val submittedAt: Instant? = null,
    val reviewedAt: Instant? = null,
    val reviewedBy: String? = null,
    val createdAt: Instant,
    val updatedAt: Instant
)

@Serializable
data class ReviewVenueDraftRequest(
    val approved: Boolean
)

@Serializable
data class UpdateVenueStatusRequest(
    val status: VenueStatus
)

@Serializable
data class VenueResponse(
    val id: String,
    val ownerId: String,
    val moderatorId: String? = null,
    val name: String,
    val description: String?,
    val category: VenueCategory,
    val phone: String,
    val website: String?,
    val email: String?,
    val location: VenueLocation,
    val schedule: VenueSchedule,
    val services: VenueServices,
    val gallery: List<VenueMediaRef>,
    val galleryMediaIds: List<String>,
    val averageRating: Double = 0.0,
    val reviewsCount: Long = 0,
    val status: VenueStatus,
    
    // Moderation Statuses
    val modGeneral: ModerationStatus,
    val modContacts: ModerationStatus,
    val modLocation: ModerationStatus,
    val modSchedule: ModerationStatus,
    val modServices: ModerationStatus,
    val modGallery: ModerationStatus,

    val createdAt: Instant,
    val updatedAt: Instant
)

@Serializable
data class VenueListResponse(
    val items: List<VenueResponse>,
    val totalCount: Long,
    val page: Int,
    val pageSize: Int
)

@Serializable
data class SavedVenueIdsResponse(
    val ids: List<String>
)

@Serializable
data class VenueServiceSuggestionsResponse(
    val items: List<String>,
    val minPrice: Double? = null,
    val maxPrice: Double? = null
)

@Serializable
data class VenueFilterRequest(
    val page: Int = 1,
    val pageSize: Int = 20,
    val query: String? = null,
    val category: VenueCategory? = null,
    val categoryGroup: VenueGroup? = null,
    val status: VenueStatus? = null,
    val ownerId: String? = null,
    val moderatorId: String? = null,
    val latitude: Double? = null,
    val longitude: Double? = null,
    val minDistanceKm: Double? = null,
    val maxDistanceKm: Double? = null,
    val serviceQueries: List<String> = emptyList(),
    val minServicePrice: Double? = null,
    val maxServicePrice: Double? = null,
    val minRating: Double? = null,
    val maxRating: Double? = null
)

@Serializable
data class VenueAssigneeResponse(
    val id: String,
    val firstName: String,
    val lastName: String,
    val role: UserRole,
    val avatarUrl: String? = null
)

@Serializable
data class VenueAssigneesResponse(
    val items: List<VenueAssigneeResponse>
)

@Serializable
data class ReassignVenueModeratorRequest(
    val moderatorId: String
)

@Serializable
enum class VenueAiImportMode {
    PREFILL,
    CREATE
}

@Serializable
data class VenueAiImportRequest(
    val websiteUrl: String,
    val mode: VenueAiImportMode = VenueAiImportMode.PREFILL,
    val uploadPhotos: Boolean = true
)

@Serializable
data class VenueAiPhotoCandidate(
    val url: String,
    val source: String? = null,
    val selected: Boolean = false,
    val uploadedMediaId: String? = null,
    val publicUrl: String? = null
)

@Serializable
data class VenueAiImportResponse(
    val draft: CreateVenueRequest,
    val createdVenue: VenueResponse? = null,
    val photoCandidates: List<VenueAiPhotoCandidate> = emptyList(),
    val uploadedPhotoMediaIds: List<String> = emptyList(),
    val warnings: List<String> = emptyList()
)
